import { Dispatch, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { FlowResponse } from '@cash-web/shared-flow-data-access-context'
import { CashResponse, HttpError } from '@cash-web/shared-util-fetch'
import { BlockerFlowInteractViewBlockerResponseStatus } from '@squareup/customer-data-events-web'

import { createErrorBlocker } from '../createErrorBlocker'
import { DEFAULT_ERROR_MESSAGE, isFlowComplete } from '../utils'
import { FlowReducerAction, FlowState } from './flowReducer'
import { handleCustomFlowResponses } from './handleCustomFlowResponses'

export const isLogicalFlowError = (response: FlowResponse) => {
  const responseContext = response.response_context
  return (
    (response.status != null && response.status !== 'SUCCESS') ||
    Boolean(responseContext?.dialog_message) ||
    Boolean(responseContext?.failure_message)
  )
}

export const useFlowResponseHandler = (state: FlowState, dispatch: Dispatch<FlowReducerAction>) => {
  const { t } = useTranslation()
  const genericFlowErrorBlockerDescriptors = useMemo(
    () => [{ id: 'flow-generic-error', blocker: { form: createErrorBlocker(t) } }],
    [t]
  )
  return async (promise: Promise<CashResponse<FlowResponse>>): Promise<FlowResponse> => {
    const validateStartFlowResponse = (response: FlowResponse) => {
      // if flow is starting and we have no blockers we should show a generic error
      if (isFlowComplete(response)) {
        return {
          ...response,
          response_context: {
            ...response.response_context,
            scenario_plan: {
              ...response.response_context?.scenario_plan,
              blocker_descriptors: genericFlowErrorBlockerDescriptors,
            },
          },
        }
      }
      return response
    }
    // only dispatch BLOCKER_SUBMIT if we are in progress
    // otherwise, we are in the loading state
    if (state.isInProgress) {
      dispatch({ type: 'BLOCKER_SUBMIT' })
    } else {
      dispatch({ type: 'SET_LOADING', isLoading: true })
    }

    try {
      const response = await promise

      if (state.isInProgress) {
        return handleFlowResponse(dispatch, response, false)
      }

      return response
    } catch (e) {
      const error = e as HttpError<FlowResponse>
      const response = error.response?.body

      if (state.isInProgress) {
        return handleFlowResponse(dispatch, response, true)
      }

      return validateStartFlowResponse(response == null || typeof response === 'string' ? {} : response)
    }
  }
}

export const handleFlowResponse = (
  dispatch: Dispatch<FlowReducerAction>,
  response: CashResponse<FlowResponse> | string | undefined,
  rejected: boolean
) => {
  let logicalStatus: BlockerFlowInteractViewBlockerResponseStatus = 'SUCCESS'
  let errorResponse: string | undefined

  if (response == null || typeof response === 'string') {
    logicalStatus = 'NETWORK_ERROR'
    errorResponse = response ?? DEFAULT_ERROR_MESSAGE
    response = {}
  }

  const responseContext = response?.response_context
  const status = response?.status
  const blockerDescriptors = response?.response_context?.scenario_plan?.blocker_descriptors
  const errorMessage = [responseContext?.dialog_message, responseContext?.failure_message].find(Boolean)
  const hasError = isLogicalFlowError(response)
  const error = hasError ? { errorCode: status, errorMessage: errorMessage ?? DEFAULT_ERROR_MESSAGE } : undefined

  if (logicalStatus === 'SUCCESS' && (hasError || rejected)) {
    logicalStatus = 'LOGICAL_ERROR'
  }
  dispatch({
    type: 'FLOW_RESPONSE',
    error: errorResponse ? { errorCode: logicalStatus, errorMessage: errorResponse } : error,
    status: logicalStatus,
    response,
  })

  if (logicalStatus === 'SUCCESS') {
    dispatch({
      type: 'BLOCKER_SUBMIT_SUCCESS',
      blockerDescriptors: blockerDescriptors ?? [],
    })
  } else {
    dispatch({
      type: 'BLOCKER_SUBMIT_ERROR',
      message: error?.errorMessage,
      blockerDescriptors,
    })
  }

  return handleCustomFlowResponses(response)
}
